import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { ChevronRight, Check, Loader2 } from 'lucide-react';
import Logo from './Logo';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * OnboardingFlow — Prompt #2 §41 first-run experience.
 *
 * 5 steps: welcome → reasons → support times → interests → done. Saves to
 * `POST /api/onboarding` on the final step. Automatically shown once when
 * a logged-in user is missing an onboarding record; skipped forever after
 * that. If the user closes without finishing, a lightweight `rbr_onb_dismissed`
 * localStorage key prevents nagging them again this session — the server
 * record is still empty so they can pick it up next session.
 */

const REASONS = [
  { id: 'stress',       label: 'Stress' },
  { id: 'better_sleep', label: 'Better sleep' },
  { id: 'healing',      label: 'Healing' },
  { id: 'self_worth',   label: 'Self-worth' },
  { id: 'peace',        label: 'Peace' },
  { id: 'motivation',   label: 'Motivation' },
  { id: 'grief',        label: 'Grief support' },
  { id: 'growth',       label: 'Personal growth' },
  { id: 'community',    label: 'Community' },
];

const SUPPORT_TIMES = [
  { id: 'morning',      label: 'Morning' },
  { id: 'during_work',  label: 'During work' },
  { id: 'afternoon',    label: 'Afternoon' },
  { id: 'evening',      label: 'Evening' },
  { id: 'before_sleep', label: 'Before sleep' },
  { id: 'whenever',     label: 'Whenever I need it' },
];

const INTERESTS = [
  { id: 'meditations',       label: 'Meditations' },
  { id: 'short_reflections', label: 'Short reflections' },
  { id: 'affirmations',      label: 'Affirmations' },
  { id: 'breathing',         label: 'Breathing' },
  { id: 'soundscapes',       label: 'Soundscapes' },
  { id: 'journaling',        label: 'Journaling' },
  { id: 'community',         label: 'Community' },
];

// Exposed so the "Personalize My Experience" settings page can reuse them.
export { REASONS, SUPPORT_TIMES, INTERESTS };

const OnboardingFlow = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);            // 0..4
  const [reasons, setReasons] = useState([]);
  const [times, setTimes] = useState([]);
  const [interests, setInterests] = useState([]);
  const [saving, setSaving] = useState(false);

  // Decide once per session whether to open the flow.
  useEffect(() => {
    if (!user) return;
    if (localStorage.getItem('rbr_onb_dismissed') === '1') return;
    const token = localStorage.getItem('rbr_token');
    if (!token) return;
    let cancelled = false;
    fetch(`${API_URL}/api/onboarding`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        // Only open if there is no completed onboarding record. Existing
        // users who already have preferences are left alone.
        if (data && !data.completed) setOpen(true);
      })
      .catch(() => { /* silent — never block the app for onboarding */ });
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const toggle = (arr, setArr, id) => setArr(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);

  const dismiss = () => {
    localStorage.setItem('rbr_onb_dismissed', '1');
    setOpen(false);
  };

  const finish = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('rbr_token');
      const r = await fetch(`${API_URL}/api/onboarding`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          reasons, supportTimes: times, interests, completed: true,
        }),
      });
      if (r.ok) {
        toast.success('Your space is ready.');
        localStorage.setItem('rbr_onb_dismissed', '1');
        setOpen(false);
      } else {
        toast.error('Could not save preferences. You can update them later in Settings.');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const total = 5;
  const progress = ((step + 1) / total) * 100;

  const ChipGrid = ({ options, selected, setSelected, testidPrefix }) => (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-grid`}>
      {options.map((o) => {
        const on = selected.includes(o.id);
        return (
          <button
            key={o.id}
            onClick={() => toggle(selected, setSelected, o.id)}
            data-testid={`${testidPrefix}-${o.id}`}
            className={`px-4 py-2.5 rounded-full text-[13px] font-medium transition-all active:scale-95 ${
              on ? 'bg-[#173F35] text-[#FFFCF6] shadow-md'
                 : 'bg-white text-stone-700 border border-stone-200 hover:border-[#C79A4A]'
            }`}
          >
            {on && <Check className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />}
            {o.label}
          </button>
        );
      })}
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[95] flex items-end sm:items-center justify-center bg-[#0D2B24]/70 backdrop-blur-md animate-fade-in" data-testid="onboarding-overlay">
      <div className="w-full max-w-[480px] bg-[#FFFCF6] rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl animate-slide-up max-h-[92vh] flex flex-col">
        {/* Progress */}
        <div className="h-1 bg-[#EFE7D4]">
          <div className="h-full bg-[#C79A4A] transition-[width] duration-300" style={{ width: `${progress}%` }} />
        </div>

        <div className="px-6 py-8 overflow-y-auto flex-1">
          {step === 0 && (
            <div className="text-center py-6" data-testid="onboarding-step-0">
              <div className="flex justify-center mb-6">
                <Logo size={80} />
              </div>
              <h2 className="text-3xl leading-tight text-stone-800 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Welcome to Reflections by Rowdy.
              </h2>
              <p className="text-[15px] text-stone-500 max-w-sm mx-auto leading-relaxed">
                A place to breathe, reflect, heal, and grow — at your pace.
              </p>
            </div>
          )}

          {step === 1 && (
            <div data-testid="onboarding-step-1">
              <p className="rbr-eyebrow text-[#C79A4A] mb-2">Step 2 of 5</p>
              <h2 className="text-2xl text-stone-800 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                What brought you here?
              </h2>
              <p className="text-sm text-stone-500 mb-5">Choose any that feel true. You can change these later.</p>
              <ChipGrid options={REASONS} selected={reasons} setSelected={setReasons} testidPrefix="onb-reason" />
            </div>
          )}

          {step === 2 && (
            <div data-testid="onboarding-step-2">
              <p className="rbr-eyebrow text-[#C79A4A] mb-2">Step 3 of 5</p>
              <h2 className="text-2xl text-stone-800 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                When do you need support most?
              </h2>
              <p className="text-sm text-stone-500 mb-5">We'll gently surface the right content at these times.</p>
              <ChipGrid options={SUPPORT_TIMES} selected={times} setSelected={setTimes} testidPrefix="onb-time" />
            </div>
          )}

          {step === 3 && (
            <div data-testid="onboarding-step-3">
              <p className="rbr-eyebrow text-[#C79A4A] mb-2">Step 4 of 5</p>
              <h2 className="text-2xl text-stone-800 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                What would you like more of?
              </h2>
              <p className="text-sm text-stone-500 mb-5">Pick the practices that call to you.</p>
              <ChipGrid options={INTERESTS} selected={interests} setSelected={setInterests} testidPrefix="onb-interest" />
            </div>
          )}

          {step === 4 && (
            <div className="text-center py-6" data-testid="onboarding-step-4">
              <div className="flex justify-center mb-6">
                <Logo size={80} />
              </div>
              <h2 className="text-3xl leading-tight text-stone-800 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
                Your space is ready.
              </h2>
              <p className="text-[15px] text-stone-500 max-w-sm mx-auto leading-relaxed">
                Reflect. Connect. Heal. Grow.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 pt-3 pb-6 border-t border-stone-200 bg-[#FFFCF6] flex-shrink-0">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={dismiss}
              data-testid="onboarding-skip"
              className="text-[12px] text-stone-500 hover:text-stone-700 underline underline-offset-4"
            >
              Skip for now
            </button>
            {step < 4 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                data-testid="onboarding-next"
                className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#173F35] text-[#FFFCF6] text-sm font-semibold hover:bg-[#0D2B24] active:scale-95 transition-all"
              >
                {step === 0 ? 'Begin' : 'Next'}
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={finish}
                disabled={saving}
                data-testid="onboarding-finish"
                className="flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#C79A4A] text-[#0D2B24] text-sm font-semibold hover:bg-[#B8873A] disabled:opacity-60 active:scale-95 transition-all"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Enter Reflections
              </button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default OnboardingFlow;
